import { fileKind, type FileEntry, type Project } from '@p2b/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorNote } from '../../App.js';
import { api } from '../../api.js';
import { fill, formatBytes, formatDate, label, useI18n } from '../../i18n.js';
import { Icon } from '../../ui.js';
import { buildTree, folderAt, isWorkspaceFile, type Folder } from './model.js';

const byName = (a: string, b: string) => a.localeCompare(b, 'is');
const baseName = (path: string) => path.slice(path.lastIndexOf('/') + 1);
const ext = (path: string) => {
  const n = baseName(path);
  const i = n.lastIndexOf('.');
  return i > 0
    ? n
        .slice(i + 1)
        .slice(0, 4)
        .toUpperCase()
    : '';
};
const META_ORDER = [
  'title',
  'sheetNumber',
  'scale',
  'storey',
  'status',
  'date',
  'designer',
  'type',
  'description',
  'case',
  'archive',
  'schema',
  'role',
  'products',
  'validation',
  'author',
];
const IMAGE = /\.(png|jpe?g|webp|gif|svg)$/i;

function FolderNav({
  folder,
  open,
  toggle,
  current,
  go,
  depth,
}: {
  folder: Folder;
  open: Set<string>;
  toggle(path: string): void;
  current: string;
  go(path: string): void;
  depth: number;
}) {
  const folders = [...folder.folders.values()].sort((a, b) => byName(a.name, b.name));
  if (!folders.length) return null;
  return (
    <ul className="folder-nav" role={depth === 0 ? 'tree' : 'group'}>
      {folders.map((f) => {
        const isOpen = open.has(f.path);
        return (
          <li key={f.path} role="treeitem" aria-expanded={f.folders.size ? isOpen : undefined}>
            <div
              className={`folder-row${current === f.path ? ' current' : ''}`}
              style={{ paddingLeft: 4 + depth * 14 }}
            >
              <button
                className="folder-caret"
                tabIndex={-1}
                aria-hidden="true"
                style={{ visibility: f.folders.size ? 'visible' : 'hidden' }}
                onClick={() => toggle(f.path)}
              >
                <Icon.Chevron className={`caret${isOpen ? ' open' : ''}`} width={13} height={13} />
              </button>
              <button
                className="folder-go"
                aria-current={current === f.path ? 'true' : undefined}
                onClick={() => go(f.path)}
              >
                <Icon.Folder width={15} height={15} />
                <span className="folder-name">{f.name}</span>
                <span className="folder-count">{f.count}</span>
              </button>
            </div>
            {isOpen && (
              <FolderNav folder={f} open={open} toggle={toggle} current={current} go={go} depth={depth + 1} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function FilesTab({ project }: { project: Project }) {
  const { t, lang } = useI18n();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  // ?folder=inbox opens a folder (the home page's "Bíður þín" links to partners' uploads).
  const [current, setCurrent] = useState(
    () => new URLSearchParams(window.location.search).get('folder') ?? '',
  );
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const files = useQuery({
    queryKey: ['files', project.id, q, kind, showDeleted],
    queryFn: () =>
      api.allFiles(project.id, {
        ...(q ? { q } : {}),
        ...(kind ? { kind: kind as FileEntry['kind'] } : {}),
        ...(showDeleted ? { includeDeleted: 'true' as never } : {}),
      }),
  });
  const all = useMemo(() => files.data ?? [], [files.data]);
  const workspaceCount = all.filter(isWorkspaceFile).length;
  const visible = useMemo(
    () => (showWorkspace ? all : all.filter((f) => !isWorkspaceFile(f))),
    [all, showWorkspace],
  );
  const tree = useMemo(() => buildTree(visible), [visible]);
  const folder = folderAt(tree, current) ?? tree;
  const searching = q.length > 0 || kind !== '';
  const go = (path: string) => {
    setCurrent(path);
    // Opening a folder opens its parents in the tree.
    setOpen((s) => {
      const n = new Set(s);
      const parts = path.split('/').filter(Boolean);
      for (let i = 1; i <= parts.length; i++) n.add(`${parts.slice(0, i).join('/')}/`);
      return n;
    });
  };
  const toggle = (p: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(p)) n.delete(p);
      else n.add(p);
      return n;
    });
  const subfolders = searching ? [] : [...folder.folders.values()].sort((a, b) => byName(a.name, b.name));
  const rows = (searching ? visible : folder.files).slice().sort((a, b) => byName(a.path, b.path));
  const crumbs = current.split('/').filter(Boolean);

  return (
    <div className="files2">
      <div className="toolbar">
        <label className="search grow">
          <Icon.Search className="search-icon" />
          <input
            type="search"
            aria-label={t('search')}
            placeholder={t('search')}
            title={t('searchHint')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <select aria-label={t('kind')} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t('allKinds')}</option>
          {fileKind.options.map((k) => (
            <option key={k} value={k}>
              {label(t, 'kind', k)}
            </option>
          ))}
        </select>
        <label className="check">
          <input
            type="checkbox"
            checked={showWorkspace}
            onChange={(e) => setShowWorkspace(e.target.checked)}
          />
          {t('showWorkspace')}
        </label>
        <label className="check">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          {t('showDeleted')}
        </label>
      </div>
      <div className="files-frame">
        <nav className="folders-pane" aria-label={t('files')}>
          <div className={`folder-row root${current === '' ? ' current' : ''}`}>
            <button
              className="folder-go"
              aria-current={current === '' ? 'true' : undefined}
              onClick={() => go('')}
            >
              <Icon.Folder width={15} height={15} />
              <span className="folder-name">{t('allFiles')}</span>
              <span className="folder-count">{tree.count}</span>
            </button>
          </div>
          <FolderNav folder={tree} open={open} toggle={toggle} current={current} go={go} depth={0} />
        </nav>
        <div className="files-main">
          <div className="files-crumbs">
            {searching ? (
              <b>{fill(t('results'), { n: rows.length })}</b>
            ) : (
              <>
                <button className="crumb-link" onClick={() => go('')} disabled={!crumbs.length}>
                  {t('allFiles')}
                </button>
                {crumbs.map((c, i) => (
                  <span key={i} className="crumb-part">
                    <span className="faint">/</span>
                    <button
                      className="crumb-link"
                      onClick={() => go(`${crumbs.slice(0, i + 1).join('/')}/`)}
                      disabled={i === crumbs.length - 1}
                    >
                      {c}
                    </button>
                  </span>
                ))}
              </>
            )}
          </div>
          <ErrorNote error={files.error} />
          {files.isPending ? (
            <p className="muted pad">{t('loading')}</p>
          ) : (
            <div className="files-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th className="hide-sm">{t('kind')}</th>
                    <th className="num">{t('ver')}</th>
                    <th className="num">{t('size')}</th>
                    <th className="hide-sm">{t('updated')}</th>
                    <th className="hide-md">{t('by')}</th>
                    <th className="hide-sm">{t('visibility')}</th>
                  </tr>
                </thead>
                <tbody>
                  {subfolders.map((f) => (
                    <tr key={f.path} className="clickable" onClick={() => go(f.path)}>
                      <td>
                        <button className="file-name" onClick={() => go(f.path)}>
                          <Icon.Folder width={17} height={17} className="faint" />
                          <span>{f.name}</span>
                        </button>
                      </td>
                      <td className="hide-sm muted" />
                      <td className="num faint">{f.count}</td>
                      <td className="num" />
                      <td className="hide-sm" />
                      <td className="hide-md" />
                      <td className="hide-sm" />
                    </tr>
                  ))}
                  {rows.map((f) => (
                    <tr
                      key={f.id}
                      className={`clickable${selected === f.path ? ' selected' : ''}${f.deletedAt ? ' deleted' : ''}`}
                      onClick={() => setSelected(f.path)}
                    >
                      <td>
                        <button className="file-name" onClick={() => setSelected(f.path)}>
                          <span className="file-ext">{ext(f.path) || '·'}</span>
                          <span>
                            {baseName(f.path)}
                            {searching && f.path.includes('/') && (
                              <span className="file-dir">{f.path.slice(0, f.path.lastIndexOf('/'))}</span>
                            )}
                          </span>
                        </button>
                      </td>
                      <td className="hide-sm muted nowrap">{label(t, 'kind', f.kind)}</td>
                      <td className="num">{f.current ? `v${f.current.version}` : ''}</td>
                      <td className="num nowrap">{f.current ? formatBytes(f.current.size, lang) : ''}</td>
                      <td className="hide-sm muted nowrap">
                        {f.current ? formatDate(f.current.createdAt, lang) : ''}
                      </td>
                      <td className="hide-md muted nowrap">{f.current?.createdByName ?? ''}</td>
                      <td className="hide-sm nowrap">
                        <Visibility v={f.visibility} />
                      </td>
                    </tr>
                  ))}
                  {!subfolders.length && !rows.length && (
                    <tr>
                      <td colSpan={7} className="muted empty-cell">
                        {all.length ? t('emptyFolder') : t('noFiles')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {!showWorkspace && workspaceCount > 0 && (
        <p className="basis">{fill(t('workspaceHidden'), { n: workspaceCount })}</p>
      )}
      {selected && <FileDrawer project={project} path={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Visibility({ v }: { v: FileEntry['visibility'] }) {
  const { t } = useI18n();
  return v === 'internal' ? (
    <span className="internal-mark">
      <Icon.Lock width={13} height={13} />
      {t('internal')}
    </span>
  ) : (
    <span className="faint">{t('publishable')}</span>
  );
}

/** A file's details beside the list: preview, actions, facts, metadata and its history. */
function FileDrawer({ project, path, onClose }: { project: Project; path: string; onClose(): void }) {
  const { t } = useI18n();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [path, onClose]);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer" ref={ref} tabIndex={-1} aria-label={baseName(path)}>
        <header className="drawer-head">
          <span className="file-ext">{ext(path) || '·'}</span>
          <div className="drawer-title">
            <h3 className="break">{baseName(path)}</h3>
            {path.includes('/') && (
              <p className="faint small break">{path.slice(0, path.lastIndexOf('/'))}</p>
            )}
          </div>
          <button className="icon-button" aria-label={t('close')} onClick={onClose}>
            <Icon.Close />
          </button>
        </header>
        <div className="drawer-body">
          <FileDetail project={project} path={path} />
        </div>
      </aside>
    </>
  );
}

function FileDetail({ project, path }: { project: Project; path: string }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const caps = new Set(project.capabilities ?? []);
  const canWrite = caps.has('write:all') || (caps.has('write:inbox') && path.startsWith('inbox/'));
  const hist = useQuery({
    queryKey: ['versions', project.id, path],
    queryFn: () => api.versions(project.id, path),
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['versions', project.id, path] });
    void qc.invalidateQueries({ queryKey: ['files', project.id] });
  };
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.patchFile>[2]) => api.patchFile(project.id, path, body),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });
  const del = useMutation({ mutationFn: () => api.deleteFile(project.id, path), onSuccess: refresh });
  useEffect(() => setEditing(false), [path]);
  if (hist.isPending) return <p className="muted">{t('loading')}</p>;
  if (hist.error) return <ErrorNote error={hist.error} />;
  const { file, versions } = hist.data;
  const cur = file.current;

  function saveMetadata() {
    try {
      const parsed = JSON.parse(draft || '{}');
      const flat: Record<string, unknown> = { ...parsed };
      // Keys removed in the editor are removed from the metadata.
      for (const k of Object.keys(cur?.metadata ?? {})) if (!(k in parsed)) flat[k] = null;
      patch.mutate({ metadata: flat });
    } catch (err) {
      alert((err as Error).message);
    }
  }

  return (
    <div className="file-detail">
      {IMAGE.test(file.path) && !file.deletedAt && (
        <div className="file-preview">
          <img src={api.fileHref(project.id, file.path)} alt="" loading="lazy" />
        </div>
      )}
      <div className="row">
        <a className="button primary" href={api.fileHref(project.id, file.path)} download>
          <Icon.Download />
          {t('download')}
        </a>
        {canWrite && !file.deletedAt && (
          <>
            {caps.has('read:all') && (
              <button
                onClick={() =>
                  patch.mutate({ visibility: file.visibility === 'internal' ? 'publishable' : 'internal' })
                }
              >
                {file.visibility === 'internal' ? t('makePublishable') : t('makeInternal')}
              </button>
            )}
            <button
              onClick={() => {
                setDraft(JSON.stringify(cur?.metadata ?? {}, null, 2));
                setEditing(true);
              }}
            >
              {t('editMetadata')}
            </button>
            <button className="quiet danger" onClick={() => confirm(t('confirmDelete')) && del.mutate()}>
              {t('markDeleted')}
            </button>
          </>
        )}
      </div>
      <ErrorNote error={patch.error ?? del.error} />
      <dl className="facts">
        <dt>{t('kind')}</dt>
        <dd>{label(t, 'kind', file.kind)}</dd>
        <dt>{t('visibility')}</dt>
        <dd>
          {file.visibility === 'internal' ? (
            <span className="internal-mark">
              <Icon.Lock width={13} height={13} />
              {t('internalLong')}
            </span>
          ) : (
            t('publishableLong')
          )}
        </dd>
        {cur && (
          <>
            <dt>{t('version')}</dt>
            <dd>
              v{cur.version}, {formatDate(cur.createdAt, lang)}
              {cur.createdByName && `, ${cur.createdByName}`}{' '}
              <span className="faint">({cur.createdVia})</span>
            </dd>
            <dt>{t('size')}</dt>
            <dd>{formatBytes(cur.size, lang)}</dd>
          </>
        )}
        {file.deletedAt && (
          <>
            <dt>{t('deleted')}</dt>
            <dd>{formatDate(file.deletedAt, lang)}</dd>
          </>
        )}
        {cur?.source && (
          <>
            <dt>{t('source')}</dt>
            <dd className="break">
              {cur.source.url ? (
                <a href={cur.source.url} target="_blank" rel="noreferrer">
                  {cur.source.url}
                </a>
              ) : (
                JSON.stringify(cur.source)
              )}
            </dd>
          </>
        )}
      </dl>
      <section>
        <h4 className="sub-title">{t('metadata')}</h4>
        {editing ? (
          <div className="stack">
            <textarea
              className="mono"
              rows={12}
              aria-describedby="metadata-help"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <p id="metadata-help" className="muted small">
              {t('metadataHelp')}
            </p>
            <div className="row">
              <button className="primary" onClick={saveMetadata} disabled={patch.isPending}>
                {t('save')}
              </button>
              <button className="quiet" onClick={() => setEditing(false)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <MetadataTable data={cur?.metadata ?? {}} />
        )}
      </section>
      <section>
        <h4 className="sub-title">{t('versions')}</h4>
        <ol className="history">
          {versions.map((v, i) => (
            <li key={v.id}>
              <span className="history-v">v{v.version}</span>
              <div>
                {formatDate(v.createdAt, lang)}
                {v.createdByName && <span className="muted">, {v.createdByName}</span>}
                {(i === 0 || v.note) && (
                  <div className="muted small">
                    {[i === 0 ? t('latest') : null, v.note].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <span className="num faint small nowrap">{formatBytes(v.size, lang)}</span>
              <a
                className="icon-button"
                href={api.fileHref(project.id, file.path, v.version)}
                title={t('download')}
                aria-label={`${t('download')} v${v.version}`}
              >
                <Icon.Download width={16} height={16} />
              </a>
            </li>
          ))}
        </ol>
      </section>
      {cur && <p className="mono faint small break">SHA-256 {cur.sha256}</p>}
    </div>
  );
}

function MetadataTable({ data }: { data: Record<string, unknown> }) {
  const { t } = useI18n();
  const rows = Object.entries(data).flatMap(([k, v]) =>
    k === 'extra' && v && typeof v === 'object'
      ? Object.entries(v as Record<string, unknown>).map(([ek, ev]) => [ek, ev] as const)
      : [[k, v] as const],
  );
  if (rows.length === 0) return <p className="muted">—</p>;
  // Known keys first, in the order a title block reads; the rest after, as stored.
  const rank = (k: string) => {
    const i = META_ORDER.indexOf(k);
    return i < 0 ? META_ORDER.length : i;
  };
  rows.sort((a, b) => rank(a[0]) - rank(b[0]));
  return (
    <dl className="facts">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt>{label(t, 'meta', k)}</dt>
          <dd className="break">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
